type SurfaceConfig = {
  isLegacy: boolean;
  isEnabled: boolean;
};

export type BrandConfig = {
  isBrandRefresh: boolean;
  surfaceConfig: SurfaceConfig;
  brandCode?: SupportedBrand;
};

const defaultSurfaceConfig = {
  isLegacy: false,
  isEnabled: true,
} as const satisfies SurfaceConfig;

export const defaultConfig = {
  isBrandRefresh: false,
  surfaceConfig: defaultSurfaceConfig,
} as const satisfies BrandConfig;

export type SupportedBrand = Omit<BrandCode, 'HH'>;

const legacySurfaceConfig = {
  ...defaultConfig,
  surfaceConfig: {
    ...defaultConfig.surfaceConfig,
    isLegacy: true,
  },
} as const satisfies BrandConfig;

export const DEV_BRAND_CONFIG = new Map<SupportedBrand, BrandConfig>([
  ['AQ', defaultConfig],
  ['CB', legacySurfaceConfig],
  ['CH', defaultConfig],
  ['DT', defaultConfig],
  ['EE', defaultConfig],
  ['EJ', defaultConfig],
  ['ES', defaultConfig],
  ['EY', defaultConfig],
  ['GI', defaultConfig],
  ['GU', defaultConfig],
  ['GV', defaultConfig],
  ['H4L', defaultConfig],
  ['HI', defaultConfig],
  ['HP', defaultConfig],
  ['HT', defaultConfig],
  ['HW', defaultConfig],
  ['ID', defaultConfig],
  ['LX', defaultConfig],
  ['ND', defaultConfig],
  ['OL', defaultConfig],
  ['OU', defaultConfig],
  ['PE', defaultConfig],
  ['PO', defaultConfig],
  ['PY', defaultConfig],
  ['QQ', defaultConfig],
  ['RU', defaultConfig],
  ['SA', defaultConfig],
  ['UA', defaultConfig],
  ['UD', defaultConfig],
  ['UP', defaultConfig],
  ['WA', defaultConfig],
  ['WW', defaultConfig],
  ['YH', defaultConfig],
]);

/**
 * Brands still on legacy surface tokens.
 * Keep in sync with `isLegacy: true` entries above (and Storybook manager copies).
 * Hardcoded — deriving via Map.entries()/filter crashes under Cypress CT webpack.
 */
export const LEGACY_SURFACE_BRAND_CODES = new Set<string>(['CB']);

export const PROD_BRAND_CONFIG = new Map<SupportedBrand, BrandConfig>([...DEV_BRAND_CONFIG]);

type ConfigEnv = 'dev' | 'prod';

const configMap = new Map<ConfigEnv, Map<SupportedBrand, BrandConfig>>([
  ['dev', DEV_BRAND_CONFIG],
  ['prod', PROD_BRAND_CONFIG],
]);

export type GetBrandConfigReturn = BrandConfig & {
  brandCode: SupportedBrand;
};

const getSafeAppEnv = (): ConfigEnv => {
  const safeAppEnv = process.env.APP_ENV ?? '';
  return ['stg', 'prd'].includes(safeAppEnv) ? 'prod' : 'dev';
};

const DEFAULT_BRAND_CODE = 'HI';
const DEPRECATED_BRAND_CODES = ['HH'];

function getSafeBrandCode(brandCode?: BrandCode): SupportedBrand {
  if (!brandCode) {
    return DEFAULT_BRAND_CODE;
  }
  if (DEPRECATED_BRAND_CODES.includes(brandCode)) {
    return DEFAULT_BRAND_CODE;
  }
  return brandCode;
}

export function getBrandConfig(
  unsafeBrandCode?: StringWithAutocompleteOptions<BrandCode>
): GetBrandConfigReturn {
  const brandCode = getSafeBrandCode(unsafeBrandCode?.toUpperCase() as BrandCode);
  const configEnv = getSafeAppEnv();
  const brandConfig = configMap.get(configEnv)?.get(brandCode) || defaultConfig;
  return {
    brandCode,
    ...brandConfig,
  };
}
